import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { TimeLogsService, TimeLog, CreateTimeLogData } from '../../services/timelogsService';
import './Meditation.css';

interface MeditationProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface AudioTrack {
  name: string;
  file: string;
}

const MEDITATION_TRACKS: AudioTrack[] = [
  { name: 'Evening Improvisation', file: '/sounds/Evening-Improvisation-with-Ethera(chosic.com).mp3' },
  { name: 'Golden Hour', file: '/sounds/Golden-Hour-chosic.com_.mp3' },
  { name: 'Sakuya', file: '/sounds/PerituneMaterial_Sakuya2(chosic.com).mp3' },
  { name: 'Beach Waves', file: '/sounds/beach-waves(chosic.com).mp3' },
  { name: 'River Stream', file: '/sounds/cagancelik__river-stream-subtle-slow-gentle(chosic.com).mp3' },
  { name: 'Time to Think', file: '/sounds/purrple-cat-time-to-think(chosic.com).mp3' },
  { name: 'Crying Sky', file: '/sounds/Crying_Sky-chosic.com_.mp3' },
  { name: 'Big Rain Drops', file: '/sounds/Big-rain-drops(chosic.com).mp3' },
  { name: 'Rain Sound and Rainforest', file: '/sounds/Rain-Sound-and-Rainforest(chosic.com).mp3' },
  { name: "Don't Forget Me", file: '/sounds/Dont-Forget-Me-Alternative-Version-from-2018(chosic.com).mp3' }
];

type TabType = 'stats' | 'timer' | 'music' | 'history';

const Meditation: React.FC<MeditationProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement>(null);
  
  const [activeTab, setActiveTab] = useState<TabType>('timer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Timer state
  const [timer, setTimer] = useState({
    isRunning: false,
    startTime: null as number | null,
    elapsedTime: 0,
    activityName: 'Meditation',
    description: ''
  });
  
  // Audio state
  const [audioState, setAudioState] = useState({
    selectedTrack: MEDITATION_TRACKS[0].file,
    isPlaying: false,
    volume: 0.5,
    loop: true
  });
  
  // History and stats
  const [meditationLogs, setMeditationLogs] = useState<TimeLog[]>([]);
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalMinutes: 0,
    todayMinutes: 0,
    thisWeekMinutes: 0
  });

  // Load meditation history on mount
  useEffect(() => {
    if (user?.id) {
      loadMeditationHistory();
    }
  }, [user?.id]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (timer.isRunning && timer.startTime) {
      interval = setInterval(() => {
        setTimer(prev => ({
          ...prev,
          elapsedTime: Date.now() - prev.startTime!
        }));
      }, 100);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer.isRunning, timer.startTime]);

  // Audio effect
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = audioState.volume;
    audio.loop = audioState.loop;

    if (audioState.isPlaying) {
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        setError('Failed to play audio. Some browsers require user interaction first.');
      });
    } else {
      audio.pause();
    }
  }, [audioState.isPlaying, audioState.volume, audioState.loop, audioState.selectedTrack]);

  const loadMeditationHistory = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const response = await TimeLogsService.getUserTimeLogs(user.id, {
        activity_type: 'meditation',
        limit: 100
      });
      
      const logs = response.data;
      setMeditationLogs(logs);
      
      // Calculate stats
      const today = new Date().toISOString().split('T')[0];
      const todayLogs = logs.filter(log => 
        new Date(log.start_time).toISOString().split('T')[0] === today
      );
      
      const thisWeekLogs = logs.filter(log => TimeLogsService.isThisWeek(log));
      
      setStats({
        totalSessions: logs.length,
        totalMinutes: logs.reduce((sum, log) => sum + log.duration_minutes, 0),
        todayMinutes: todayLogs.reduce((sum, log) => sum + log.duration_minutes, 0),
        thisWeekMinutes: thisWeekLogs.reduce((sum, log) => sum + log.duration_minutes, 0)
      });
    } catch (err: any) {
      console.error('Error loading meditation history:', err);
      setError(err.message || 'Failed to load meditation history');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    if (!timer.activityName.trim()) {
      setError('Please enter a meditation name');
      return;
    }
    
    setTimer(prev => ({
      ...prev,
      isRunning: true,
      startTime: Date.now(),
      elapsedTime: 0
    }));
    setError(null);
    
    // Optionally start audio when timer starts
    if (!audioState.isPlaying) {
      setAudioState(prev => ({ ...prev, isPlaying: true }));
    }
  };

  const stopTimer = async () => {
    if (!timer.isRunning || !user) return;
    
    const durationMinutes = Math.floor(timer.elapsedTime / 60000);
    
    if (durationMinutes < 1) {
      setError('Meditation session must be at least 1 minute');
      return;
    }
    
    try {
      setLoading(true);
      
      const endTime = new Date();
      const startTime = new Date(timer.startTime!);
      
      const timeLogData: CreateTimeLogData = {
        user_id: user.id,
        activity_type: 'meditation',
        activity_id: `meditation_${Date.now()}`,
        activity_name: timer.activityName,
        duration_minutes: durationMinutes,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        description: timer.description || undefined,
        is_manual_entry: false
      };
      
      await TimeLogsService.createTimeLog(timeLogData);
      
      // Reset timer
      setTimer({
        isRunning: false,
        startTime: null,
        elapsedTime: 0,
        activityName: 'Meditation',
        description: ''
      });
      
      // Reload history
      await loadMeditationHistory();
      
      setError(null);
    } catch (err: any) {
      console.error('Error saving meditation session:', err);
      setError(err.message || 'Failed to save meditation session');
    } finally {
      setLoading(false);
    }
  };

  const toggleAudio = () => {
    setAudioState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
  };

  const handleTrackChange = (trackFile: string) => {
    setAudioState(prev => ({ ...prev, selectedTrack: trackFile }));
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(e.target.value);
    setAudioState(prev => ({ ...prev, volume }));
  };

  const handleDeleteLog = async (timelogId: string) => {
    if (!window.confirm('Delete this meditation session?')) return;
    
    try {
      await TimeLogsService.deleteTimeLog(timelogId);
      await loadMeditationHistory();
    } catch (err: any) {
      console.error('Error deleting log:', err);
      setError(err.message || 'Failed to delete session');
    }
  };

  if (!user) {
    return (
      <div className="meditation-container">
        <div className="meditation-header">
          <button className="menu-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>🧘 /meditation</h1>
        </div>
        <div className="meditation-content">
          <p>Please sign in to use the meditation module.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="meditation-container">
      <audio
        ref={audioRef}
        src={audioState.selectedTrack}
        onEnded={() => {
          if (!audioState.loop) {
            setAudioState(prev => ({ ...prev, isPlaying: false }));
          }
        }}
      />
      
      <div className="meditation-header">
        <div className="header-left">
          <button className="menu-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>🧘 /meditation</h1>
        </div>
        <div className="header-right">
          <button className="logs-btn" onClick={() => navigate('/logs')}>📋</button>
        </div>
      </div>

      <div className="meditation-content">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="meditation-tab-content">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Sessions</div>
                <div className="stat-value">{stats.totalSessions}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Time</div>
                <div className="stat-value">{TimeLogsService.formatDuration(stats.totalMinutes)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Today</div>
                <div className="stat-value">{TimeLogsService.formatDuration(stats.todayMinutes)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">This Week</div>
                <div className="stat-value">{TimeLogsService.formatDuration(stats.thisWeekMinutes)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Timer Tab */}
        {activeTab === 'timer' && (
          <div className="meditation-tab-content">
            <div className="timer-wrapper">
              <div className="timer-display-circle">
                <div className="timer-time-large">
                  {formatTime(timer.elapsedTime)}
                </div>
                <div className="timer-status-text">
                  {timer.isRunning ? 'Meditating...' : 'Ready'}
                </div>
              </div>
              
              <div className="timer-controls">
                <div className="timer-inputs">
                  <input
                    type="text"
                    placeholder="Meditation name"
                    value={timer.activityName}
                    onChange={(e) => setTimer(prev => ({ ...prev, activityName: e.target.value }))}
                    disabled={timer.isRunning}
                    className="meditation-name-input"
                  />
                  
                  <textarea
                    placeholder="Notes (optional)"
                    value={timer.description}
                    onChange={(e) => setTimer(prev => ({ ...prev, description: e.target.value }))}
                    disabled={timer.isRunning}
                    className="meditation-notes-input"
                    rows={3}
                  />
                </div>
                
                <div className="timer-buttons">
                  {!timer.isRunning ? (
                    <button 
                      className="start-btn-meditation"
                      onClick={startTimer}
                      disabled={!timer.activityName.trim()}
                    >
                      ▶ Start Session
                    </button>
                  ) : (
                    <button 
                      className="stop-btn-meditation"
                      onClick={stopTimer}
                      disabled={loading}
                    >
                      ⏹ End Session
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Music Tab */}
        {activeTab === 'music' && (
          <div className="meditation-tab-content">
            <div className="audio-player">
              <div className="audio-controls">
                <select
                  value={audioState.selectedTrack}
                  onChange={(e) => handleTrackChange(e.target.value)}
                  className="track-selector"
                >
                  {MEDITATION_TRACKS.map(track => (
                    <option key={track.file} value={track.file}>
                      {track.name}
                    </option>
                  ))}
                </select>
                
                <button 
                  className={`audio-play-btn ${audioState.isPlaying ? 'playing' : ''}`}
                  onClick={toggleAudio}
                >
                  {audioState.isPlaying ? '⏸ Pause' : '▶ Play'}
                </button>
                
                <div className="volume-control">
                  <label>Volume:</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={audioState.volume}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                  />
                  <span>{Math.round(audioState.volume * 100)}%</span>
                </div>
                
                <label className="loop-checkbox">
                  <input
                    type="checkbox"
                    checked={audioState.loop}
                    onChange={(e) => setAudioState(prev => ({ ...prev, loop: e.target.checked }))}
                  />
                  Loop
                </label>
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="meditation-tab-content">
            {loading ? (
              <div className="loading-message">Loading meditation history...</div>
            ) : meditationLogs.length === 0 ? (
              <div className="empty-state">
                <p>No meditation sessions yet. Start your first session!</p>
              </div>
            ) : (
              <div className="history-list">
                {meditationLogs.map(log => (
                  <div key={log.timelog_id} className="history-card">
                    <div className="history-card-header">
                      <div className="history-name">{log.activity_name}</div>
                      <div className="history-duration">
                        {TimeLogsService.formatDuration(log.duration_minutes)}
                      </div>
                    </div>
                    <div className="history-card-details">
                      <div className="history-date">
                        {TimeLogsService.formatDate(log.start_time)}
                      </div>
                      <div className="history-time">
                        {TimeLogsService.formatTime(log.start_time)} - {TimeLogsService.formatTime(log.end_time)}
                      </div>
                      {log.description && (
                        <div className="history-description">{log.description}</div>
                      )}
                    </div>
                    <div className="history-card-actions">
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteLog(log.timelog_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation Bar */}
      <div className="meditation-bottom-nav">
        <button
          className={`meditation-nav-item ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <span className="meditation-nav-label">/stats</span>
        </button>
        <button
          className={`meditation-nav-item ${activeTab === 'timer' ? 'active' : ''}`}
          onClick={() => setActiveTab('timer')}
        >
          <span className="meditation-nav-label">/timer</span>
        </button>
        <button
          className={`meditation-nav-item ${activeTab === 'music' ? 'active' : ''}`}
          onClick={() => setActiveTab('music')}
        >
          <span className="meditation-nav-label">/music</span>
        </button>
        <button
          className={`meditation-nav-item ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <span className="meditation-nav-label">/history</span>
        </button>
      </div>
    </div>
  );
};

export default Meditation;

